import * as ents from '@attain-apps/entities'

import type { File } from './file'

/** Prepared File Finder hierarchy. */
export interface FileTree {
  readonly groups: readonly FileTree.Group[]
}

export namespace FileTree {
  /** A downloadable leaf in the File Finder hierarchy. */
  export interface Entry {
    readonly file: File
    readonly id: string
    readonly name: string
  }

  /** An expandable group in the File Finder hierarchy. */
  export interface Group {
    readonly files: readonly Entry[]
    readonly groups: readonly Group[]
    readonly id: string
    readonly name: string
  }

  interface GroupBuilder {
    readonly files: Entry[]
    readonly groups: Map<string, GroupBuilder>
    readonly id: string
    readonly name: string
  }

  /** Builds the app, scenario, qualifier, and file hierarchy for File Finder. */
  export function from(
    files: readonly File[],
    apps: readonly ents.core.admin.App.ForUser[],
  ): FileTree {
    const groups = new Map<string, GroupBuilder>()

    for (const file of files) {
      const group = getFileGroup(groups, file, apps)
      group.files.push({
        file,
        id: ents.core.common.fileManagement.Key.from({
          ...file,
          purpose: ents.core.common.fileManagement.Purpose.FileManagement,
        }),
        name: file.extension ? `${file.id}.${file.extension}` : file.id,
      })
    }

    return {
      groups: Array.from(groups.values(), finalizeGroup).sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
    }
  }

  /** Finds the app, scenario, and qualifier group that owns a file. */
  function getFileGroup(
    groups: Map<string, GroupBuilder>,
    file: File,
    apps: readonly ents.core.admin.App.ForUser[],
  ): GroupBuilder {
    if (!ents.core.admin.Context.ForApp.isForApp(file.context)) {
      return getQualifierGroup(
        getOrCreateGroup(groups, 'zone', 'Zone files'),
        file.qualifier,
      )
    }

    const { appId } = file.context
    const scenarioId = ents.core.admin.Context.ForScenario.isForScenario(
      file.context,
    )
      ? file.context.scenarioId
      : undefined
    const app = apps.find(({ id }) => id === appId)
    let group = getOrCreateGroup(groups, `app:${appId}`, app?.name ?? appId)
    if (scenarioId) {
      const scenario = app?.scenarios.find(({ id }) => id === scenarioId)
      group = getOrCreateGroup(
        group.groups,
        `scenario:${scenarioId}`,
        scenario?.name ?? scenarioId,
      )
    }

    return getQualifierGroup(group, file.qualifier)
  }

  /** Adds the qualifier level when the key carries one. */
  function getQualifierGroup(
    group: GroupBuilder,
    qualifier: string | undefined,
  ): GroupBuilder {
    return qualifier
      ? getOrCreateGroup(
          group.groups,
          `qualifier:${qualifier}`,
          `Q: ${qualifier}`,
        )
      : group
  }

  /** Retrieves an existing group or creates it in its parent collection. */
  function getOrCreateGroup(
    groups: Map<string, GroupBuilder>,
    id: string,
    name: string,
  ): GroupBuilder {
    const existing = groups.get(id)
    if (existing) {
      return existing
    }

    const group: GroupBuilder = {
      files: [],
      groups: new Map(),
      id,
      name,
    }
    groups.set(id, group)
    return group
  }

  /** Converts the mutable tree builder into an immutable presentation group. */
  function finalizeGroup(group: GroupBuilder): Group {
    return {
      files: [...group.files].sort((a, b) => a.name.localeCompare(b.name)),
      groups: Array.from(group.groups.values(), finalizeGroup).sort((a, b) =>
        a.name.localeCompare(b.name),
      ),
      id: group.id,
      name: group.name,
    }
  }
}
